"use client";

import { c as _c } from "react/compiler-runtime";
import { cx } from "@/lib/tailwind-utils";
interface SenderFromFieldProps {
  /** Editable display name, pre-filled from the clinic setting. */
  name: string;
  onNameChange: (value: string) => void;
  /** Editable local-part (before "@"), pre-filled from the clinic setting. */
  localPart: string;
  onLocalPartChange: (value: string) => void;
  /** Verified sending domain; falls back to a placeholder when none is configured. */
  domain: string;
  /** Placeholder for the local-part (the tenant subdomain). */
  placeholder: string;
  className?: string;
}

// Plain inline text, matching the "To:" row: no border, no box, no focus ring.
// border-0 / p-0 / focus:ring-0 strip the @tailwindcss/forms defaults; text-body
// overrides the plugin's forced 1rem so it matches the surrounding rows.
const bareInput = "col-start-1 row-start-1 w-full min-w-0 border-0 bg-transparent p-0 text-body text-foreground " + "outline-none placeholder:text-foreground-muted focus:ring-0";

/**
 * An input that auto-sizes to its content (CSS-only): an invisible mirror span sizes
 * the grid cell to the text, and the overlaid input fills it. Keeps the field hugging
 * its value so it reads as inline text with no trailing gap.
 */
function AutoWidthInput(t0) {
  const $ = _c(12);
  const {
    value,
    onChange,
    placeholder,
    ariaLabel
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== ariaLabel || $[3] !== placeholder || $[4] !== t1 || $[5] !== value) {
    t2 = <input aria-label={ariaLabel} value={value} onChange={t1} placeholder={placeholder} size={1} autoComplete="off" spellCheck={false} data-1p-ignore={true} data-lpignore="true" className={bareInput} />;
    $[2] = ariaLabel;
    $[3] = placeholder;
    $[4] = t1;
    $[5] = value;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = value || placeholder;
  let t4;
  if ($[7] !== t3) {
    t4 = <span aria-hidden="true" className="col-start-1 row-start-1 invisible whitespace-pre text-body">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <span className="inline-grid">{t2}{t4}</span>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/**
 * The sending domain is fixed (not editable here), so when it runs long we
 * middle-truncate it — keeping the start and the TLD — to keep the From line on
 * one tidy row. The full value stays available via the title tooltip.
 */
function truncateDomain(domain: string, max = 28): string {
  if (domain.length <= max) return domain;
  const keep = max - 1; // one slot for the ellipsis
  const head = Math.ceil(keep * 0.6);
  const tail = keep - head;
  return `${domain.slice(0, head)}…${domain.slice(-tail)}`;
}

/**
 * Compact per-message "From" line for the composers. Both the display name and the
 * address local-part default to the clinic settings and can be edited for this one message.
 */
export function SenderFromField(t0) {
  const $ = _c(23);
  const {
    name,
    onNameChange,
    localPart,
    onLocalPartChange,
    domain,
    placeholder,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("flex flex-wrap items-center gap-x-1 gap-y-1 text-body", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="mr-0.5 text-foreground-muted">From:</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== name || $[4] !== onNameChange) {
    t3 = <AutoWidthInput ariaLabel="Sender name" value={name} onChange={onNameChange} placeholder="Sender name" />;
    $[3] = name;
    $[4] = onNameChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-foreground-muted">{"<"}</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onLocalPartChange) {
    t5 = value => onLocalPartChange(value.replace(/\s+/g, "-"));
    $[7] = onLocalPartChange;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== localPart || $[10] !== placeholder || $[11] !== t5) {
    t6 = <AutoWidthInput ariaLabel="Sending address" value={localPart} onChange={t5} placeholder={placeholder} />;
    $[9] = localPart;
    $[10] = placeholder;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = domain || undefined;
  const t8 = domain || "your-domain.com";
  let t9;
  if ($[13] !== t8) {
    t9 = truncateDomain(t8);
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t9) {
    t10 = <span className="whitespace-nowrap text-foreground-muted" title={t7}>@{t9}{">"}</span>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t1 || $[19] !== t10 || $[20] !== t3 || $[21] !== t6) {
    t11 = <div className={t1}>{t2}{t3}{t4}{t6}{t10}</div>;
    $[18] = t1;
    $[19] = t10;
    $[20] = t3;
    $[21] = t6;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}